"use client"

import {
    buildJobylonFilters,
    type JobylonBlockFilters
} from "@/app/(site)/_components/SanityBlocks/JobylonBlock/buildJobylonFilters"
import {
    clearJobylonWidget,
    mountJobylonWidget
} from "@/app/(site)/_components/SanityBlocks/JobylonBlock/mountJobylonWidget"
import {
    getJobylonWidgetTargetId,
    type JobylonEmbedConfig
} from "@/app/(site)/_components/SanityBlocks/JobylonBlock/types"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { t } from "@/translations"
import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"

type JobylonBlockData = {
    _type: "jobylonBlock"
    _key?: string
    companyId?: number
    pageSize?: number
    filters?: JobylonBlockFilters
}

type JobylonBlockProps = {
    data: JobylonBlockData
    params?: { locale: string }
}

function getConfigCacheKey(data: JobylonBlockData, iframeTitle: string) {
    return JSON.stringify({
        blockKey: data._key,
        companyId: data.companyId,
        pageSize: data.pageSize ?? 10,
        filters: buildJobylonFilters(data.filters),
        iframeTitle
    })
}

function getEmbedConfig(data: JobylonBlockData): JobylonEmbedConfig | null {
    if (!data.companyId) {
        return null
    }

    return {
        companyId: data.companyId,
        pageSize: data.pageSize ?? 10,
        filters: buildJobylonFilters(data.filters)
    }
}

export default function JobylonBlock({ data, params }: JobylonBlockProps) {
    const pathname = usePathname()
    const langCode = getLangCode(params?.locale ?? "") || "en"
    const iframeTitle = t(langCode, "jobylon.iframe_title")
    const configCacheKey = getConfigCacheKey(data, iframeTitle)
    const containerRef = useRef<HTMLDivElement>(null)
    const messagingCleanupRef = useRef<(() => void) | null>(null)
    const targetId = getJobylonWidgetTargetId(data._key)

    useEffect(() => {
        const container = containerRef.current
        const embedConfig = getEmbedConfig(data)

        if (!container || !embedConfig) {
            return
        }

        const mountWidget = () => {
            messagingCleanupRef.current?.()
            messagingCleanupRef.current = mountJobylonWidget(
                container,
                embedConfig,
                targetId,
                iframeTitle
            )
        }

        mountWidget()

        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                mountWidget()
            }
        }

        window.addEventListener("pageshow", onPageShow)

        return () => {
            window.removeEventListener("pageshow", onPageShow)
            messagingCleanupRef.current?.()
            messagingCleanupRef.current = null
            clearJobylonWidget(container)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configCacheKey, pathname])

    if (!data.companyId) {
        return null
    }

    return (
        <div className="w-full px-12 md:px-16">
            <div
                ref={containerRef}
                id={targetId}
                aria-label={iframeTitle}
                className="min-h-[240px] w-full"
            />
        </div>
    )
}
